import type { Editor } from '@tiptap/react';
import * as React from 'react';

type BubbleMenuPlacement = {
  placement: 'top' | 'bottom' | 'auto';
  offset: [number, number];
};

type UseBubbleMenuPlacementParams = {
  isPhone: boolean;
  editor: Editor | null;
};

/**
 * Хук для позиционирования BubbleMenu.
 *
 * Tippy.js (используется в Tiptap 2.x) имеет встроенные модификаторы:
 * - `flip` — автоматически переворачивает меню при нехватке места
 * - `preventOverflow` — не даёт выйти за границы viewport
 *
 * Однако есть нюанс с Android: нативное меню выделения текста всегда
 * появляется СВЕРХУ над выделением. Чтобы наше меню не пересекалось
 * с ним, на Android принудительно ставим placement: 'bottom'.
 *
 * На iOS и desktop используем 'auto' — Tippy сам выберет лучшую сторону.
 */
export const useBubbleMenuPlacement = ({ isPhone, editor }: UseBubbleMenuPlacementParams): BubbleMenuPlacement => {
  const [isAndroid, setIsAndroid] = React.useState(false);

  React.useEffect(() => {
    if (typeof navigator === 'undefined') {
      return;
    }

    const ua = navigator.userAgent || '';
    const navWithData = navigator as Navigator & { userAgentData?: { platform?: string } };
    const platform = navWithData.userAgentData?.platform;
    const platformValue = platform?.toLowerCase() ?? '';
    const isAndroidDevice = /Android/i.test(ua) || platformValue === 'android';

    setIsAndroid(isAndroidDevice);
  }, []);

  const isMobile = isPhone || isAndroid;

  // Пересчёт позиции BubbleMenu при изменении viewport (клавиатура на мобилках).
  // Tippy сам слушает resize, но visualViewport события (scroll при появлении
  // клавиатуры) могут потребовать принудительного update.
  React.useEffect(() => {
    if (!isMobile || !editor) {
      return;
    }

    const vv = window.visualViewport;

    const handleViewportChange = () => {
      // Принудительно обновляем позицию Tippy через ProseMirror
      editor.view.dispatch(editor.view.state.tr);
    };

    vv?.addEventListener('resize', handleViewportChange);
    vv?.addEventListener('scroll', handleViewportChange);

    return () => {
      vv?.removeEventListener('resize', handleViewportChange);
      vv?.removeEventListener('scroll', handleViewportChange);
    };
  }, [editor, isMobile]);

  // Android: принудительно bottom, чтобы не пересекаться с нативным меню выделения
  // iOS/Phone: auto с увеличенным offset (нативное меню iOS появляется над текстом,
  //            но 'auto' + больший offset обычно достаточно)
  // Desktop: top с обычным offset
  if (isAndroid) {
    return { placement: 'bottom', offset: [0, 12] };
  }

  if (isMobile) {
    return { placement: 'auto', offset: [0, 12] };
  }

  return { placement: 'top', offset: [0, 8] };
};
